/**
 * Target Validation Calculator - Pure functions for bond target compliance calculations
 *
 * This module provides Level 5 pure functions for calculating target compliance metrics
 * including shortfalls, excess amounts, and target achievement status.
 */

/**
 * Calculates the shortfall between target and current amount
 * @param targetAmount - Target principal amount
 * @param currentAmount - Current principal amount
 * @returns Shortfall amount (positive if below target, 0 otherwise)
 * @pure Level 5 calculation - no side effects
 */
export function calculateTargetShortfall(targetAmount: number, currentAmount: number): number {
  const shortfall = targetAmount - currentAmount;
  return Math.max(0, shortfall);
}

/**
 * Calculates the excess amount over target
 * @param currentAmount - Current principal amount
 * @param targetAmount - Target principal amount
 * @returns Excess amount (positive if above target, 0 otherwise)
 * @pure Level 5 calculation - no side effects
 */
export function calculateTargetExcess(currentAmount: number, targetAmount: number): number {
  const excess = currentAmount - targetAmount;
  return Math.max(0, excess);
}

/**
 * Calculates DRF available capacity considering minimum balance and safety buffer
 * @param drfBalance - Current DRF balance
 * @param minimumBalance - Required minimum DRF balance
 * @param safetyBuffer - Additional safety buffer amount
 * @returns Available capacity for use
 * @pure Level 5 calculation - no side effects
 */
export function calculateDrfAvailableCapacity(
  drfBalance: number,
  minimumBalance: number,
  safetyBuffer: number = 0
): number {
  return Math.max(0, drfBalance - minimumBalance - safetyBuffer);
}

/**
 * Determines if a target is met within tolerance
 * @param currentAmount - Current amount
 * @param targetAmount - Target amount
 * @param tolerance - Acceptable tolerance for target achievement
 * @returns True if target is met within tolerance
 * @pure Level 5 calculation - no side effects
 */
export function isTargetMetWithinTolerance(
  currentAmount: number,
  targetAmount: number,
  tolerance: number = 0
): boolean {
  const shortfall = targetAmount - currentAmount;
  return shortfall <= tolerance;
}
